/**
 * @module Experince/Product-Selector
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
// tslint:disable max-line-length
// Interfaces
import {
  LineDeivceDetails,
  lineTypeEnum,
  TileIdAndLengthToCreateLine,
} from '@experience/shop/product-selector/interfaces/product-selector.interface';
import {
  Cart,
  CartItem,
  CartLine,
} from '@support/api/proxies/responses/api-cart-response.interface';
import { ByodDeviceData } from '@experience/shop/byod/interfaces/byod-landing.interface';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';

// Models
import { CreateDeleteUpdateTileModel } from '@experience/shop/product-selector/models/create-update-delete-tile.model';
import { ExtractFromCartResponseModel } from '@experience/shop/product-selector/models/extract-from-cart-response';
import { ExtractByodDataModel } from '@experience/shop/product-selector/models/extract-byod-data.model';
import { IsLineSetupCompletedModel } from '@experience/shop/product-selector/models/isLineSetupCompleted.model';
import { ItemDetail } from '@support/api/common/product.interface';

export class ProductSelectorLandingModel {
  constructor(
    public objectService: ObjectService,
    private stateStoreUtilService: StateStoreUtilsService
  ) {}

  /**
   * Function to create new tile or non line tile
   * @param {TileIdAndLengthToCreateLine} tileIdDetails
   * @param {lineTypeEnum} lineType
   * @param {boolean} noPlanNeeded required. Set to true for scan to cart scenario else false
   */
  public createTile(
    tileIdDetails: TileIdAndLengthToCreateLine,
    lineType: lineTypeEnum,
    noPlanNeeded: boolean,
    existingLines?: CartLine,
    deviceDetails?: ItemDetail
  ): void {
    let focusedTileDetails = CreateDeleteUpdateTileModel.createTile(
      tileIdDetails,
      lineType,
      noPlanNeeded
    );
    if (lineType === lineTypeEnum.EXISTING) {
      focusedTileDetails = ExtractFromCartResponseModel.extractPlansObject(
        existingLines,
        tileIdDetails.currentLength,
        focusedTileDetails,
        this.objectService,
        this.stateStoreUtilService
      );
      focusedTileDetails.services = {
        servicesList: this.objectService.get(existingLines, 'services.0.serviceNames'),
      };
    }
    focusedTileDetails.selectedDevice = focusedTileDetails.deviceDetails = deviceDetails;
    this.stateStoreUtilService.setFocusedTileLineNonLineItem(
      focusedTileDetails,
      focusedTileDetails.tileId
    );
  }
  /**
   * Function to map with cart response
   * @param {Cart} cartResponse
   */
  public getPlansFromCartResponse(cartResponse: Cart): void {
    if (this.objectService.get(cartResponse, 'lines.length') > 0) {
      this.mapToLineObject(this.objectService.get(cartResponse, 'lines'));
    }
  }

  /**
   * Function to map to lines object from cart response
   * @param lineObject
   */
  public mapToLineObject(lineObject: CartLine[]): void {
    const tileIdList = this.stateStoreUtilService.getTileIdList(lineTypeEnum.LINE);
    lineObject.forEach((line: CartLine, index: number) => {
      let getCurrentIdDetails = this.stateStoreUtilService.getFocusedTileLineNonLineItem(
        tileIdList[index]
      );
      getCurrentIdDetails = ExtractFromCartResponseModel.extractPlansObject(
        line,
        index + 1,
        getCurrentIdDetails,
        this.objectService,
        this.stateStoreUtilService
      );
      const serviceThresholdValue = this.stateStoreUtilService.getServiceThresholdValue(
        tileIdList[index]
      );
      if (serviceThresholdValue && getCurrentIdDetails.deviceDetails) {
        getCurrentIdDetails.deviceDetails.serviceThreshold = serviceThresholdValue;
      }

      this.stateStoreUtilService.setFocusedTileLineNonLineItem(
        getCurrentIdDetails,
        tileIdList[index]
      );
    });
  }
  /**
   * Function used to create map to non line items
   * @param {Cart} devicesFromCart
   * @param {ItemDetail} productOffers
   * @returns void
   */
  public mapToNonLineItemDevices(devicesFromCart: Cart, productOffers?: ItemDetail): void {
    if (devicesFromCart.devices.length > 0) {
      const currentNonLineItemList: string[] = this.stateStoreUtilService.getTileIdList(
        lineTypeEnum.NONLINE
      );
      currentNonLineItemList.forEach((tileId: string, index: number) => {
        this.extractNonLineItems(devicesFromCart.devices, tileId, index, productOffers);
      });
    }
  }

  /**
   * Functio used to loop non line items
   * @param {CartItem} nonLineDevices array
   * @param {titleId} titleId
   * @param {index} line index
   * @param {ItemDetail} productOffers
   * @returns void
   */
  private extractNonLineItems(
    nonLineDevices: CartItem[],
    tileId: string,
    index: number,
    productOffers?: ItemDetail
  ): void {
    let focusedTileIdDetails = this.stateStoreUtilService.getFocusedTileLineNonLineItem(tileId);
    focusedTileIdDetails.currentLineNumber = index + 1;
    focusedTileIdDetails = ExtractFromCartResponseModel.extractDeviceItems(
      focusedTileIdDetails,
      nonLineDevices[index],
      this.objectService,
      this.stateStoreUtilService,
      null
    );
    this.stateStoreUtilService.setFocusedTileLineNonLineItem(focusedTileIdDetails, tileId);
  }

  /**
   * Function to set to true if no plan is needed is selected
   * @param {boolean} noPlanNeeded
   */
  public setIsPlanNeeded(noPlanNeeded: boolean): void {
    let currentLineDetails: LineDeivceDetails =
      this.stateStoreUtilService.getFocusedTileLineNonLineItem();
    currentLineDetails = CreateDeleteUpdateTileModel.updateNoPlanNeeded(
      currentLineDetails,
      noPlanNeeded
    );
    currentLineDetails.isLineSetupCompleted = IsLineSetupCompletedModel.isLineSetupCompleted(
      currentLineDetails,
      this.objectService
    );
    this.stateStoreUtilService.setFocusedTileLineNonLineItem(currentLineDetails);
  }
  /**
   * Function to save selected simKit Device Details
   * @param {NewSimData | string} simData
   * @param {ByodDeviceData} byodModel
   */
  public setSimDetails(byodModel: ByodDeviceData, simData: string): void {
    let currentLineDetails: LineDeivceDetails =
      this.stateStoreUtilService.getFocusedTileLineNonLineItem();
    currentLineDetails = ExtractByodDataModel.extractByodData(
      byodModel,
      currentLineDetails,
      this.objectService,
      simData
    );
    this.stateStoreUtilService.setFocusedTileLineNonLineItem(currentLineDetails);
  }
  /**
   * Function to Delete Line
   */
  public deleteLine(type: lineTypeEnum): void {
    let tileIdList: string[];
    tileIdList =
      type === lineTypeEnum.LINE
        ? this.stateStoreUtilService.getTileIdList(lineTypeEnum.LINE)
        : this.stateStoreUtilService.getTileIdList(lineTypeEnum.NONLINE);
    const indexOfTileToBeRemoved: number = tileIdList.findIndex((item: string) => {
      return item === this.stateStoreUtilService.getFocusedTileId();
    });
    tileIdList.splice(indexOfTileToBeRemoved, 1);
    if (tileIdList.length > 0) {
      this.stateStoreUtilService.setTileIdList(tileIdList, type);
    } else {
      this.stateStoreUtilService.clearTileIdList(type);
    }
    if (indexOfTileToBeRemoved === 0) {
      this.setOppositeTileTypeInFocus(type, tileIdList);
    } else {
      this.stateStoreUtilService.setFocusedTileId(tileIdList[indexOfTileToBeRemoved - 1]); // Setting focus n-1 position
    }
  }

  /**
   * Function used to set focus tile to opposite line type if current tile index 0 is removed
   * @param {lineTypeEnum} type
   */
  private setOppositeTileTypeInFocus(type: lineTypeEnum, tileIdList: string[]): void {
    let oppositeTileIdList: string[];
    oppositeTileIdList =
      type === lineTypeEnum.LINE
        ? this.stateStoreUtilService.getTileIdList(lineTypeEnum.NONLINE)
        : this.stateStoreUtilService.getTileIdList(lineTypeEnum.LINE);
    if (oppositeTileIdList.length > 0) {
      this.stateStoreUtilService.setFocusedTileId(
        oppositeTileIdList[oppositeTileIdList.length - 1]
      );
    } else if (tileIdList.length > 0) {
      this.stateStoreUtilService.setFocusedTileId(tileIdList[0]);
    }
  }
}
